import { CheckIcon, PencilIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useState } from "react";

import { Badge } from "@fronterahealth/frontera-ui-components/ui/badge";
import { FieldLabel } from "@fronterahealth/frontera-ui-components/ui/field";

import {
  ReportSectionFieldsType,
  ReportSectionsType,
  useUpdateReportSectionFieldMutation,
} from "@api/graphql/types-and-hooks";
import { PeerReviewFieldLabelBadges } from "@components/QaReview/components/PeerReviewFieldLabelBadges";
import { ReportFieldLabelSuggestionBadge } from "@components/QaReview/components/ReportFieldLabelSuggestionBadge";
import { ReportFieldDropdownMenu } from "@components/ReportField/ReportFieldDropdownMenu";
import { ReportFieldLabelAiBadge } from "@components/ReportField/ReportFieldLabelAiBadge";
import { RevealOnFieldHover } from "@components/ReportField/RevealOnFieldHover";
import { useCanEditFieldTitle } from "@components/ReportField/capabilities";
import { notifyError } from "@components/notifications/notifications";
import { useReport } from "@providers/ReportProvider";

interface ReportFieldLabelProps {
  field: ReportSectionFieldsType;
  section: ReportSectionsType;
  htmlFor: string | undefined;
}

export const ReportFieldLabel: React.FC<ReportFieldLabelProps> = ({ field, section, htmlFor }) => {
  const canEditTitle = useCanEditFieldTitle(section);
  const [isEditing, setIsEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(field.title ?? "");
  const { reportQuery } = useReport();
  const updateField = useUpdateReportSectionFieldMutation({
    onSuccess: async (data) => {
      if (data.updateReportSectionField?.status) {
        setIsEditing(false);
        await reportQuery.refetch();
      } else if (data.updateReportSectionField?.status === false) {
        notifyError(data.updateReportSectionField?.message ?? "Error updating field title");
      }
    },
    onError: (error) => {
      console.error("Error while updating field title:", error);
      notifyError("Error Updating Field Title");
    },
  });

  const startEditing = () => {
    setDraftTitle(field.title ?? "");
    setIsEditing(true);
  };

  return (
    <div className="flex items-center gap-0.5">
      {isEditing ? (
        <>
          <input
            className="w-full text-sm font-normal text-text-secondary p-0 border-0 ring-0"
            autoFocus
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
          />
          <RevealOnFieldHover>
            <Badge variant="outline" size="sm" aria-label="Cancel editing" onClick={() => setIsEditing(false)}>
              <XMarkIcon data-icon="inline-start" />
            </Badge>
          </RevealOnFieldHover>
          <RevealOnFieldHover>
            <Badge
              variant="outline"
              size="sm"
              aria-label="Save label"
              onClick={() =>
                updateField.mutate({
                  reportSectionId: section.id,
                  reportSectionFieldId: field.id,
                  title: draftTitle,
                })
              }
            >
              <CheckIcon data-icon="inline-start" />
            </Badge>
          </RevealOnFieldHover>
        </>
      ) : (
        <>
          <FieldLabel htmlFor={htmlFor}>{field.title}</FieldLabel>
          {canEditTitle && (
            <RevealOnFieldHover>
              <Badge variant="outline" size="sm" aria-label="Edit label" onClick={startEditing}>
                <PencilIcon data-icon="inline-start" />
              </Badge>
            </RevealOnFieldHover>
          )}
        </>
      )}
      {(field.isRequired || field.templateSectionField?.isRequired) && (
        <span aria-label="required" className="text-destructive">
          *
        </span>
      )}
      <div className="ml-auto flex items-center gap-2">
        <ReportFieldLabelAiBadge field={field} />
        <ReportFieldLabelSuggestionBadge field={field} />
        <PeerReviewFieldLabelBadges field={field} />
        <ReportFieldDropdownMenu section={section} field={field} />
      </div>
    </div>
  );
};
